import {Inject, Injectable, Type} from '@angular/core';
import {getLockIconConfig} from '@application/helper/get-lock-icon-config';
import {LastModifiedCardUtils} from '@application/helper/last-modified-card-utils';
import {NavigationId} from '@application/helper/routing/navigation-id.enum';
import {NavigationUtils} from '@application/helper/routing/navigation-utils';
import {Permission} from '@domain/profile/permission.enum';
import {AUTHENTICATION, Authentication} from '@infrastructure/http/authentication/authentication';
import {HttpPlasticProductsService} from '@infrastructure/http/plastic-product/http-plastic-products.service';
import {
  ColDefBuilderFactoryService,
  ContentSwitcherNavigationInput,
  CrudColdef,
  GridOptionsBuilder,
  LinkIconRendererComponent,
  NoDataOverlayComponentParams,
  OverlayComponentParams,
  StringUtils,
  TranslateService
} from '@vdw/angular-component-library';
import {ICellRendererParams, RowNode, ValueGetterParams} from 'ag-grid-community';
import {Subject} from 'rxjs';
import {GridColorPreviewComponent} from '../../color/overview/grid-color-preview/grid-color-preview.component';
import {PlasticProductGridType} from './plastic-product-grid-type.enum';

@Injectable({providedIn: 'root'})
export class PlasticProductOverviewService {
  private readonly pageUrls = LastModifiedCardUtils.getPageUrls('plasticProduct');

  public constructor(
    @Inject(AUTHENTICATION) private readonly authentication: Authentication,
    private readonly colDefBuilderFactoryService: ColDefBuilderFactoryService,
    private readonly plasticProducts: HttpPlasticProductsService,
    private readonly translate: TranslateService
  ) {}

  public getColumnDefs(withLinkIcon: boolean = true, gridType: PlasticProductGridType = PlasticProductGridType.OVERVIEW, componentModule?: Type<any>, rowActionsComplete?: Subject<void>): CrudColdef {
    let nameColDef = this.colDefBuilderFactoryService.getBuilder().withHeaderName('GENERAL.NAME').withColIdAndField('name', true).withComparator(StringUtils.stringComparator);
    let productGroup = null;

    if (withLinkIcon) {
      const editRoute = NavigationUtils.getNavigationRoute(NavigationId.EDIT_PLASTIC_PRODUCT);

      nameColDef = nameColDef.withCellRenderer(LinkIconRendererComponent, (params: ICellRendererParams) => {
        return {
          params,
          editPageUrl: this.pageUrls.edit,
          rowActionsComplete,
          contentSwitcherEntry: {componentInfo: {component: editRoute.component, module: componentModule}, data: editRoute.data} as ContentSwitcherNavigationInput,
          canClick: gridType !== PlasticProductGridType.INLINE,
          lockIconConfig: getLockIconConfig(params.data.name, this.plasticProducts.getConflicts(params.data.id), rowActionsComplete)
        };
      });
    }

    if (this.authentication.getCurrentSubscription().hasPermission(Permission.PLASTIC_SELECT_PRODUCT_GROUP)) {
      productGroup = this.colDefBuilderFactoryService
        .getBuilder()
        .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.PRODUCT_GROUP')
        .withColId('productGroup')
        .withField('productGroup.name', true)
        .withComparator(StringUtils.stringComparator)
        .build();
    }

    return {
      colDefs: [
        nameColDef.build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withColId('color')
          .withField('color.name', true)
          .withHeaderName('TEXTILE_DATA.COLOR.COLOR')
          .withValueGetter((params: ValueGetterParams<any>) => params.data.color)
          .withCellRenderer(GridColorPreviewComponent)
          .withFilterValueGetter((params: ValueGetterParams) => params.data.color?.name)
          .withComparator((valueA: any, valueB: any, nodeA: RowNode, nodeB: RowNode) => StringUtils.stringComparator(valueA?.name, valueB?.name))
          .build(),
        ...(productGroup ? [productGroup] : []),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withColId('unitOfMeasurementId')
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.UNIT_OF_MEASUREMENT.UNIT_OF_MEASUREMENT')
          .withValueGetter((params: ValueGetterParams) => this.translate.instant(`TEXTILE_DATA.PLASTIC_PRODUCT.UNIT_OF_MEASUREMENT.${params.data.unitOfMeasurement}`), true)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('GENERAL.WEIGHT')
          .withColIdAndField('weightInKg', true)
          .withCellClass('right')
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.MATERIAL')
          .withColId('material')
          .withField('material.name', true)
          .withComparator(StringUtils.stringComparator)
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('CUSTOMERS.CUSTOMER')
          .withColId('customer')
          .withField('customer.name', true)
          .withComparator(StringUtils.stringComparator)
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('MACHINE.DETAILS.STANDARD_EFFICIENCY')
          .withColIdAndField('standardEfficiency', true)
          .withCellClass('right')
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('UTILITIES.TOOL.SPEED')
          .withColIdAndField('speed', true)
          .withCellClass('right')
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('UTILITIES.TOOL.SPEED_UNIT.SPEED_UNIT')
          .withColId('speedUnitId')
          .withValueGetter((params: ValueGetterParams) => this.translate.instant(`UTILITIES.TOOL.SPEED_UNIT.${params.data.speedUnit}`), true)
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('GENERAL.DESCRIPTION')
          .withColIdAndField('description', true)
          .withComparator(StringUtils.stringComparator)
          .withInitialHide(gridType === PlasticProductGridType.INLINE)
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.MOUNT_TIME', 1, false, 'GENERAL.UNIT.MINUTE')
          .withColIdAndField('mountTimeInMinutes', true)
          .withCellClass('right')
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.DISMOUNT_TIME', 1, false, 'GENERAL.UNIT.MINUTE')
          .withColIdAndField('dismountTimeInMinutes', true)
          .withCellClass('right')
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.LOAD_TIME', 1, false, 'GENERAL.UNIT.MINUTE')
          .withColIdAndField('loadTimeInMinutes', true)
          .withCellClass('right')
          .build(),
        this.colDefBuilderFactoryService
          .getBuilder()
          .withHeaderName('TEXTILE_DATA.PLASTIC_PRODUCT.UNLOAD_TIME', 1, false, 'GENERAL.UNIT.MINUTE')
          .withColIdAndField('unloadTimeInMinutes', true)
          .withCellClass('right')
          .build()
      ],
      rowActionsComplete
    } as CrudColdef;
  }

  public getDefaultGridOptionsBuilder(builder: GridOptionsBuilder): GridOptionsBuilder {
    return builder
      .withLoadingOverlay({
        scale: 0.7
      } as OverlayComponentParams)
      .withNoRowsOverlay({
        scale: 0.7,
        titleParam: this.translate.instant('TEXTILE_DATA.PLASTIC_PRODUCT.PLASTIC_PRODUCT', {count: 1}),
        hideDescription: true
      } as NoDataOverlayComponentParams);
  }
}
